'use client';

import { Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { FormField } from '../FormField';
import {
  OWNERSHIP_LABELS,
  INSTRUCTION_LABELS,
  SUB_FLOW_LABELS,
  type PropertyInstructionSectionProps
} from './constants';

type InstructionReadSectionProps = Pick<
  PropertyInstructionSectionProps,
  | 'ownershipType'
  | 'instruction'
  | 'milestoneSummary'
  | 'transferBeneficiaries'
  | 'transferSubFlow'
  | 'hasContraryIntention'
  | 'percentageOwned'
  | 'beneficiaryHoldingType'
>;

export function InstructionReadSection({
  ownershipType,
  instruction,
  milestoneSummary,
  transferBeneficiaries,
  transferSubFlow,
  hasContraryIntention,
  percentageOwned,
  beneficiaryHoldingType
}: InstructionReadSectionProps) {
  return (
    <Fragment>
      <div className="border-border-subtle w-full border-t" />
      <div className="flex w-full flex-col gap-2">
        <FormField
          label="Ownership Type"
          value={
            ownershipType
              ? (OWNERSHIP_LABELS[ownershipType] ?? ownershipType)
              : '—'
          }
          mode="read"
        />
        {ownershipType === 'tenants-in-common' && !!percentageOwned && (
          <FormField
            label="Ownership Share"
            value={`${percentageOwned}%`}
            mode="read"
          />
        )}
        <FormField
          label="Instruction"
          value={
            instruction && instruction !== 'undecided'
              ? INSTRUCTION_LABELS[instruction]
              : '—'
          }
          mode="read"
        />
        {instruction === 'transfer-to-beneficiary' && !!transferSubFlow && (
          <FormField
            label="Transfer Method"
            value={SUB_FLOW_LABELS[transferSubFlow]}
            mode="read"
          />
        )}
        {!!transferBeneficiaries &&
          transferBeneficiaries.length > 0 &&
          instruction === 'transfer-to-beneficiary' && (
            <FormField
              label="Transferring to"
              value={transferBeneficiaries
                .map((b) => {
                  const name = b.contact
                    ? `${b.contact.firstName} ${b.contact.lastName}`
                    : '';
                  if (
                    beneficiaryHoldingType === 'tenants-in-common' &&
                    b.percentage != null
                  ) {
                    return `${name} (${b.percentage}%)`;
                  }
                  return name;
                })
                .filter(Boolean)
                .join(', ')}
              mode="read"
            />
          )}
        {instruction === 'transfer-to-beneficiary' &&
          !!hasContraryIntention && (
            <FormField
              label="Contrary Intention"
              value="Estate pays off mortgage"
              mode="read"
            />
          )}
        {!!milestoneSummary && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Milestones</span>
            <Badge variant="outline" className="">
              {milestoneSummary}
            </Badge>
          </div>
        )}
      </div>
    </Fragment>
  );
}
